import AARTextArea from "@/components/AARTextArea";
import AARTextField from "@/components/AARTextField";
import BaseDrawer from "@/components/BaseDrawer";
import Btn from "@/components/Btn";
import CustomLabel from "@/components/CustomLabel";
import { ACCEPTED, SUBMITTED } from "@/constants/area";
import { LookupValueMapping } from "@/constants/lookups";
import { queryKeys } from "@/constants/queryKeyFactory";
import { ENGINEER, PROJECT_ADMIN, SUBCONTRACTOR } from "@/constants/roles";
import { OVERDUE, REASSIGNED, REJECTED, RELEASE_REJECTED } from "@/constants/tasks";
import { useAllLookups } from "@/hooks/useLookups";
import { useTaskAction } from "@/hooks/useTasks";
import { useAllUsers } from "@/hooks/useUsers";
import { ICompany } from "@/interfaces/company.interface";
import { IArea } from "@/interfaces/level.interface";
import { FileWithPreview, IRequest } from "@/interfaces/request.interface";
import { IRequestTasks, ITaskAction } from "@/interfaces/task.interface";
import { IUser } from "@/interfaces/user.interface";
import { queryClient } from "@/lib/queryClient";
import { companySvc } from "@/services/company.service";
import { tasksSvc } from "@/services/task.service";
import { useAppSelector } from "@/store/hooks/hooks";
import { hideLoader, showLoader } from "@/store/slices/loaderSlice";
import { RootState, store } from "@/store/store";
import { convertDateTime, isAuthorize } from "@/utils/common";
import { buildFilters } from "@/utils/kendo";
import ToastService from "@/utils/toast";
import { CompositeFilterDescriptor } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridItemChangeEvent,
  GridRowClickEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { Switch, TextBox, TextBoxHandle } from "@progress/kendo-react-inputs";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import {
  Download,
  EyeIcon,
  FileSpreadsheet,
  FileText,
  Paperclip,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import "react-image-crop/dist/ReactCrop.css";
import { useDispatch, useSelector } from "react-redux";
import FileUploader from "./FileUploader";
import { ASSIGNED } from "@/constants/request";

interface IProps {
  isViewMode: boolean;
  setIsViewMode: React.Dispatch<React.SetStateAction<boolean>>;
  handlePreview: any;
  readOnlyMode: boolean;
  showTaskActionDrawer: boolean;
  setShowTaskActionDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  selectedArea?: IArea;
  selectedRequest?: IRequest | null;
  selectedLevelId?: string | null;
  taskAction?: ITaskAction | null;
  setTaskAction: React.Dispatch<React.SetStateAction<ITaskAction | null>>;
  setShowDetailsDrawer: React.Dispatch<React.SetStateAction<boolean>>;
}

interface IGridData {
  name: string;
  wirNumber: string;
  isApplicable: boolean;
  inEdit?: boolean;
}

function TaskActionDrawer({
  isViewMode,
  setIsViewMode,
  handlePreview,
  showTaskActionDrawer,
  setShowTaskActionDrawer,
  selectedArea,
  selectedRequest,
  selectedLevelId,
  taskAction,
  setTaskAction,
  setShowDetailsDrawer,
  ...drawerProps
}: IProps) {
  const dispatch = useDispatch();
  const isGridEditing = useRef(false);
  const [showDialog, setShowDialog] = useState(false);
  const { user, userGroups } = useSelector((state: RootState) => state.user);
  const selectedProject = useAppSelector(
    (state: RootState) => state.projects.selectedProject
  );

  const { data: activities } = useAllLookups(
    {
      filter: buildFilters(selectedProject?.id, undefined, false, [
        { field: "type", operator: "eq", value: LookupValueMapping.Activities },
      ]),
    },
    { enabled: showTaskActionDrawer }
  );

  // Build user filter based on current user's role
  let userFilters: CompositeFilterDescriptor = {
    logic: "and",
    filters: [
      {
        field: "id",
        operator: "ne",
        value: taskAction?.userId || "",
      },
    ],
  };
  if (user?.roles?.includes(SUBCONTRACTOR) || user?.roles?.includes(ENGINEER)) {
    userFilters.filters.unshift({
      logic: "and",
      filters: [
        {
          field: "companyId",
          operator: "eq",
          value: taskAction?.companyId || "",
        },
      ],
    });
    userFilters.filters.push({
      logic: "and",
      filters: [
        {
          field: "roles",
          operator: "array-contains",
          value: SUBCONTRACTOR,
        },
      ],
    });
  } else if (user?.roles?.includes("Super Admin")) {
    userFilters.filters.push({
      logic: "and",
      filters: [
        {
          field: "isActive",
          operator: "eq",
          value: "true",
        },
      ],
    });
  } else if (user?.roles?.includes("Project Admin")) {
    if (selectedProject?.id) {
      userFilters.filters.push({
        logic: "and",
        filters: [
          {
            field: "projects",
            operator: "array-contains",
            value: selectedProject.id,
          },
        ],
      });
    }
  } else if (user?.roles?.includes("Engineer")) {
    userFilters.filters.push({
      logic: "and",
      filters: [
        {
          field: "roles",
          operator: "array-contains",
          value: "Engineer",
        },
      ],
    });
    if (selectedProject?.id) {
      userFilters.filters.push({
        logic: "and",
        filters: [
          {
            field: "projects",
            operator: "array-contains",
            value: selectedProject.id,
          },
        ],
      });
    }
  }

  const { data: users, refetch } = useAllUsers(
    {
      filter: userFilters,
    },
    {
      enabled: showTaskActionDrawer && !!taskAction,
      showLoader: false,
    }
  );

  const taskActionMutation = useTaskAction({
    showLoader: false,
  });

  const [error, setError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<FileWithPreview[]>([]);
  const [invalidFields, setInvalidFields] = useState<{
    [key: string]: boolean;
  }>({});
  const [data, setData] = useState<IGridData[]>([]);
  const [editID, setEditID] = useState<string | null>(null);

  // Check if attachments are required based on user role
  const isAttachmentRequired = () => {
    // Attachments are optional for Engineer role, mandatory for others
    if(taskAction?.action === "accept") {
      return false;
    }
    return !user?.roles?.includes(ENGINEER);
  };

  const validateSave = () => {
    if (taskAction?.action === "release") {
      if (!taskAction?.comments) return false;

      const hasValidActivities = data?.some(
        (activity) => activity.isApplicable && activity.wirNumber
      );
      if (!hasValidActivities) return false;

      // Check attachments for release action if required for the user's role
      if (isAttachmentRequired() && uploadedFiles.length === 0) {
        return false;
      }
      return true;
    }

    // For "action" type, validate reason field
    if (taskAction?.action === "action") {
      if (!taskAction?.reason || taskAction?.reason.trim() === "") {
        return false;
      }
    }

    // Check attachments only if required for the user's role
    if (isAttachmentRequired() && uploadedFiles.length === 0) {
      return false;
    }
    return true;
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setTaskAction((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (!showTaskActionDrawer) {
      setIsViewMode(false);
      setError(null);
      setIsSubmitting(false);
    } else {
      refetch();
    }
  }, [showTaskActionDrawer]);

  useEffect(() => {
    if (!showTaskActionDrawer) return;
    if (taskAction?.companyId) {
      dispatch(showLoader());
      companySvc
        .getById(taskAction?.companyId)
        .then((res) => {
          if (res.data as ICompany) {
            const data = res.data as unknown as ICompany;
          }
        })
        .finally(() => {
          dispatch(hideLoader());
        });
    }
  }, [taskAction?.companyId, showTaskActionDrawer]);

  useEffect(() => {
    if (taskAction?.activities && showTaskActionDrawer) {
      var data = taskAction?.activities?.map((actvitiy) => {
        var exists = taskAction?.activities.find(
          (x) => x.name === actvitiy.name
        );
        return {
          name: actvitiy.name,
          isApplicable:
            exists != null && exists != undefined ? exists.isApplicable : false,
          wirNumber:
            exists != null && exists != undefined ? exists.wirNumber : "",
        };
      });

      setData(data);
    }
  }, [taskAction?.activities, showTaskActionDrawer]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (approvalAction: string) => {

    if (taskAction?.action === "reassign" && approvalAction !== "delegate") {
      if (
        approvalAction === "reassign" &&
        (taskAction?.comments === "" || uploadedFiles?.length === 0)
      ) {
        ToastService.error(
          "Comments and attachments are required for reassign"
        );
        return;
      }
    }

    if (taskAction!.action === "release" && approvalAction === "reject") {
      setShowTaskActionDrawer(false);
      return;
    }

    if (
      taskAction?.action === "accept" &&
      (approvalAction === "reject" || approvalAction === "partial")
    ) {
      if (uploadedFiles?.length === 0 || taskAction?.comments === "") {
        ToastService.error(
          "Attachments and comments are required for rejection"
        );
        return;
      }
    }

    if (taskAction!.action === "action" && approvalAction === "reject") {
      if (!taskAction?.reason || taskAction?.reason?.trim() === "") {
        ToastService.error("Please enter the reason for rejection");
        return;
      }
    }

    if (taskAction!.action === "release" && approvalAction !== "delegate") {
      if (data?.filter((x) => x.isApplicable).some((x) => x.wirNumber === "")) {
        ToastService.error(
          "Please enter the WIR Number for all applicable Activities"
        );
        return;
      }

      const seen = new Set();
      const hasDuplicate = data
        ?.filter((x) => x.isApplicable)
        .some((item) => {
          if (seen.has(item.wirNumber)) {
            return true;
          }
          seen.add(item.wirNumber);
          return false;
        });

      if (hasDuplicate) {
        ToastService.error(
          "WIR Number for all applicable Actvities should be unique"
        );
        return;
      }
    }

    setIsSubmitting(true);

    const actionPayload: ITaskAction = {
      ...taskAction,
      type: taskAction?.action === "view" ? "action" : taskAction!.action,
      levelId: selectedLevelId!,
      requestId: taskAction!.requestId,
      companyId: taskAction!.companyId,
      userId: taskAction!.userId,
      userName: taskAction!.userName,
      status: taskAction!.status,
      attachmentUrls: taskAction!.attachmentUrls,
      action: approvalAction,
      isResubmit: taskAction!.isResubmit,
      activities:
        taskAction?.action === "release"
          ? data.map((trade) => ({
              name: trade.name,
              isApplicable: trade.isApplicable,
              wirNumber: trade.wirNumber,
            })) || []
          : taskAction?.activities!,
      assignedToDetails: taskAction?.assignedToDetails!,
      createdByDetails: taskAction?.createdByDetails!,
      delegateTo: selectedUser?.id || taskAction?.delegateTo,
    };

    store.dispatch(showLoader());

    taskActionMutation.mutate(actionPayload, {
      onSuccess: (response) => {
        const res = response.data as IRequestTasks;

        if (uploadedFiles.length > 0) {
          tasksSvc
            .uploadAttachments(res.id!, uploadedFiles)
            .then((res) => {
              ToastService.success(
                `${
                  taskAction!.action === "accept" &&
                  (approvalAction === "approve" || approvalAction === "partial")
                    ? "AAR has been accepted"
                    : approvalAction === "delegate"
                    ? "Request has been delegated"
                    : taskAction!.action === "accept" &&
                      approvalAction === "reject"
                    ? "AAR has been rejected"
                    : taskAction!.action === "release"
                    ? "Release has been submitted"
                    : "Request has been " +
                      (approvalAction == "approve"
                        ? "approved"
                        : approvalAction == "delegate"
                        ? "delegated"
                        : "rejected")
                } successfully`
              );
            })
            .finally(() => {
              queryClient.invalidateQueries({
                queryKey: queryKeys.tasks.request(taskAction!.requestId),
              });
              setUploadedFiles([]);
              setTaskAction(null);
              setData([]);
              setShowTaskActionDrawer(false);
              setSelectedUser(null);
              setShowDialog(false);
            });
        } else {
          ToastService.success(
            `${
              taskAction!.action === "accept" &&
              (approvalAction === "approve" || approvalAction === "partial")
                ? "AAR has been accepted"
                : approvalAction === "delegate"
                ? "Request has been delegated"
                : taskAction!.action === "accept" && approvalAction === "reject"
                ? "AAR has been rejected"
                : taskAction!.action === "release"
                ? "Release has been submitted"
                : "Request has been " +
                  (approvalAction == "approve"
                    ? "approved"
                    : approvalAction == "delegate"
                    ? "delegated"
                    : "rejected")
            } successfully`
          );

          queryClient.invalidateQueries({
            queryKey: queryKeys.tasks.request(taskAction!.requestId),
          });

          setTaskAction(null);
          setData([]);
          setShowTaskActionDrawer(false);
          setSelectedUser(null);
          setShowDialog(false);
        }
      },

      onSettled: () => {},
    });
  };

  const isApplicableCell = (props: GridCellProps) => {
    const { dataItem, field } = props;
    const fieldName = field as keyof typeof dataItem;

    const handleEnableDisable = async (e: any) => {
      const newValue = e.target.value;
      setData((prev) => {
        return prev.map((item) =>
          item.name === dataItem.name
            ? { ...item, [fieldName]: newValue }
            : item
        );
      });
    };

    return (
      <td>
        <Switch
          checked={props.dataItem.isApplicable}
          onLabel="Yes"
          offLabel="No"
          name="isApplicable"
          value={!props.dataItem.isApplicable}
          onChange={handleEnableDisable}
          disabled={taskAction?.action !== "release"}
        ></Switch>
      </td>
    );
  };

  const WIRCell = (props: GridCellProps) => {
    const { dataItem, field } = props;
    const fieldName = field as keyof typeof dataItem;
    const dataValue = dataItem[fieldName];
    const inputRef = useRef<TextBoxHandle | null>(null);
    useEffect(() => {
      if (
        dataItem.name === editID &&
        inputRef.current &&
        isGridEditing.current
      ) {
        inputRef.current.element?.focus();
      }
    }, [editID, isGridEditing.current]);

    const onChange = (e: any) => {
      const newValue = e.target.value;
      setData((prevData) =>
        prevData.map((item) =>
          item.name === dataItem.name
            ? { ...item, [fieldName]: newValue }
            : item
        )
      );
    };
    return (
      <td>
        {dataItem.name === editID ? (
          <TextBox
            style={{ width: "100%" }}
            value={props.dataItem.wirNumber}
            onChange={onChange}
            disabled={
              taskAction?.action !== "release" || !props.dataItem.isApplicable
            }
            ref={inputRef}
            onFocus={() => (isGridEditing.current = true)}
          />
        ) : (
          dataValue
        )}
      </td>
    );
  };

  const itemChange = (event: GridItemChangeEvent) => {
    const inEditID = event.dataItem.name;
    const field = event.field || "";
    const newData = data.map((item) => {
      if (item.name === inEditID) {
        return { ...item, [field]: event.value };
      }
      return item;
    });

    setData(newData);
  };

  const rowClick = (event: GridRowClickEvent) => {
    if (taskAction?.action === "release") {
      setEditID(event.dataItem.name);
    }
  };
  const [activeTab, setActiveTab] = useState(0);

  const isCommentsRequired = () => {
    if (taskAction?.action === "release") return true;
    return false;
  };

  const renderDrawerContent = () => (
    <>
      <div className="grid gap-4 -mt-2 tabs-section">
        <TabStrip
          tabPosition={"top"}
          selected={activeTab}
          onSelect={(e) => setActiveTab(e.selected)}
        >
          <TabStripTab
            title={
              <span className="text-txtPrimary inline-flex items-center gap-2">
                <img src="./images/details-icon.png" alt="" />
                Details
              </span>
            }
            contentClassName="w-full"
          >
            <div className="border border-[#E0E5F2] p-4 w-full mt-4">
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <AARTextField
                      name="createdBy"
                      value={taskAction?.createdByName}
                      label="Assigned By"
                      placeholder=""
                      disabled={true}
                      required={false}
                      onValidate={() => {}}
                      onChange={() => {}}
                      onFocus={() => (isGridEditing.current = false)}
                    />
                  </div>
                  <div>
                    <AARTextField
                      name="createdOn"
                      value={
                        taskAction?.createdOn
                          ? convertDateTime(taskAction?.createdOn)
                          : ""
                      }
                      label="Assigned On"
                      placeholder=""
                      disabled={true}
                      required={false}
                      onValidate={() => {}}
                      onChange={() => {}}
                    />
                  </div>
                  <div>
                    <AARTextField
                      name="requestWirNo"
                      value={taskAction?.requestWirNumber}
                      label="Request WIR number."
                      placeholder=""
                      disabled={true}
                      required={false}
                      onValidate={() => {}}
                      onChange={() => {}}
                      onFocus={() => (isGridEditing.current = false)}
                    />
                  </div>
                  {(taskAction?.action === "accept" ||
                    taskAction?.action == "reassign") && (
                    <div>
                      <AARTextField
                        name="activities"
                        value={taskAction?.activities
                          .map((x) => x.name)
                          .join(", ")}
                        label="Activities"
                        placeholder=""
                        disabled={true}
                        required={false}
                        onValidate={() => {}}
                        onChange={() => {}}
                        onFocus={() => (isGridEditing.current = false)}
                      />
                    </div>
                  )}
                </div>

                {taskAction?.action === "action" && (
                  <div>
                    <AARTextArea
                      name="comments"
                      value={taskAction?.comments || ""}
                      onChange={handleChange}
                      onValidate={(name, isValid) =>
                        // setInvalidFields((prev) => ({
                        //   ...prev,
                        //   [name]: !isValid,
                        // }))
                        console.log(name, isValid)
                      }
                      label="Comments"
                      required={isCommentsRequired()}
                      placeholder="Enter Comments"
                      disabled={taskAction?.action === "action"}
                      error=""
                      onFocus={() => (isGridEditing.current = false)}
                    />
                  </div>
                )}
              </div>
              {taskAction?.action === "reassign" && (
                <div className="mt-4">
                  <AARTextArea
                    name="previousComments"
                    value={taskAction?.previousComments || ""}
                    onChange={handleChange}
                    onValidate={(name, isValid) =>
                      // setInvalidFields((prev) => ({
                      //   ...prev,
                      //   [name]: !isValid,
                      // }))
                      console.log(name, isValid)
                    }
                    label="Rejection Comments"
                    required={false}
                    placeholder="Enter Comments"
                    disabled={true}
                    error=""
                    onFocus={() => (isGridEditing.current = false)}
                  />
                </div>
              )}
            </div>

            <div className="border border-[#E0E5F2] p-4 w-full mt-4">
              {taskAction?.action !== "accept" &&
                taskAction?.status !== REJECTED &&
                taskAction?.status !== REJECTED &&
                taskAction?.status !== REASSIGNED && (
                  <div>
                    <Grid
                      style={{ width: "100%", maxHeight: "250px" }}
                      scrollable="scrollable"
                      data={data}
                      onItemChange={itemChange}
                      onRowClick={rowClick}
                      editField="inEdit"
                    >
                      <GridToolbar className="py-1">
                        <div className="text-[14px] font-medium text-txtPrimary flex">
                          <img
                            src="./images/assignments-icon.png"
                            alt=""
                            className="mr-2 w-4 h-4"
                          />
                          <p>Activities</p>
                        </div>
                      </GridToolbar>
                      <GridColumn field="name" title="Activity" width={250} />
                      <GridColumn
                        field="isApplicable"
                        title="Applicable"
                        cells={{
                          data: isApplicableCell,
                        }}
                      />
                      <GridColumn
                        field="wirNumber"
                        title="WIR No."
                        width={250}
                        cells={{
                          data: WIRCell,
                        }}
                      />
                    </Grid>
                  </div>
                )}
              {taskAction?.action !== "action" && (
                <div className="mt-4">
                  <AARTextArea
                    name="comments"
                    value={taskAction?.comments || ""}
                    onChange={handleChange}
                    onValidate={(name, isValid) =>
                      // setInvalidFields((prev) => ({
                      //   ...prev,
                      //   [name]: !isValid,
                      // }))
                      console.log(name, isValid)
                    }
                    label="Comments"
                    required={isCommentsRequired()}
                    placeholder="Enter Comments"
                    disabled={isViewMode}
                    error=""
                    onFocus={() => (isGridEditing.current = false)}
                  />
                </div>
              )}
              {(taskAction?.action === "action" ||
                (taskAction?.status === RELEASE_REJECTED && isViewMode)) && (
                <div className="mt-4">
                  <AARTextArea
                    name="reason"
                    value={taskAction?.reason || ""}
                    onChange={handleChange}
                    onValidate={(name, isValid) =>
                      setInvalidFields((prev) => ({
                        ...prev,
                        [name]: !isValid,
                      }))
                    }
                    label="Reason"
                    required={true}
                    placeholder="Enter Reason"
                    disabled={isViewMode}
                    error="Invalid Reason"
                    onFocus={() => (isGridEditing.current = false)}
                  />
                </div>
              )}

              {isViewMode && (
                <div>
                  {taskAction?.attachmentUrls?.length ? (
                    <div className="mt-4">
                      <p className="font-medium text-[16px] text-[#2B3674]">
                        Attachments
                      </p>
                      <div className="grid grid-cols-6 gap-3 mt-4">
                        {taskAction?.attachmentUrls.map((file) => {
                          // Function to detect file type from URL
                          const getFileType = (url: any) => {
                            const extension = url
                              .split(".")
                              .pop()
                              .toLowerCase();
                            if (extension === "pdf") {
                              return "pdf";
                            } else if (
                              ["xls", "xlsx", "csv"].includes(extension)
                            ) {
                              return "excel";
                            } else if (
                              ["jpg", "jpeg", "png", "gif", "webp"].includes(
                                extension
                              )
                            ) {
                              return "image";
                            } else {
                              return "unknown";
                            }
                          };

                          const fileType = getFileType(file);

                          if (fileType === "pdf" || fileType === "excel") {
                            return (
                              <div
                                key={file}
                                className="relative border rounded-md overflow-hidden group w-[100%] h-[150px] bg-gray-100 flex items-center justify-center"
                              >
                                {fileType === "pdf" ? (
                                  <FileText
                                    size={48}
                                    className="text-primary-500"
                                  />
                                ) : (
                                  <FileSpreadsheet
                                    size={48}
                                    className="text-primary-500"
                                  />
                                )}

                                <div className="absolute bottom-2 left-0 right-0 text-center text-xs text-gray-600 truncate px-2">
                                  {file.split("/").pop()}
                                </div>

                                <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/70 transition-all duration-500 opacity-0 invisible group-hover:opacity-100 group-hover:visible">
                                  {/* <Button
                            className="rounded p-2 text-white border border-white"
                            fillMode={"outline"}
                            onClick={() => handlePreview(file)}
                          >
                            <EyeIcon size={16} className="w-4 h-4" />
                          </Button> */}
                                  <Button
                                    className="rounded p-2 text-white border border-white"
                                    fillMode={"outline"}
                                    onClick={() => {
                                      // Handle file download here
                                      const link = document.createElement("a");
                                      link.href = file;
                                      link.download =
                                        file.split("/").pop() || "file";
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <Download size={16} className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            );
                          }

                          // Default image rendering (existing code)
                          return (
                            <div
                              key={file}
                              className="relative border rounded-md overflow-hidden group w-[100%] h-[150px]"
                            >
                              <img
                                src={file}
                                className="object-cover h-full w-full"
                              />
                              <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/70 transition-all duration-500 opacity-0 invisible group-hover:opacity-100 group-hover:visible">
                                <Button
                                  className="rounded p-2 text-white border border-white"
                                  fillMode={"outline"}
                                  onClick={() => handlePreview(file)}
                                >
                                  <EyeIcon size={16} className="w-4 h-4" />
                                </Button>
                                <Button
                                  className="rounded p-2 text-white border border-white"
                                  fillMode={"outline"}
                                  onClick={() => {
                                    // Handle file download here
                                    const link = document.createElement("a");
                                    link.href = file;
                                    link.download =
                                      file.split("/").pop() || "file";
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }}
                                >
                                  <Download size={16} className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              )}

              {!isViewMode && (
                <div className="mt-4">
                  <FileUploader
                    required={isAttachmentRequired()}
                    label="Attach Images/Files"
                    onFilesChange={setUploadedFiles}
                    files={uploadedFiles}
                    maxFileSize={5}
                    setError={setError}
                    error={error}
                  />
                </div>
              )}
            </div>
          </TabStripTab>
          {!isViewMode && (
            <TabStripTab
              title={
                <span className="text-txtPrimary inline-flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attachments
                </span>
              }
              contentClassName="w-full"
            >
              {taskAction?.status === SUBMITTED &&
              selectedRequest?.attachmentUrls?.length ? (
                <div className="grid grid-cols-6 gap-3 mt-4">
                  {selectedRequest?.attachmentUrls.map((file) => {
                    // Function to detect file type from URL
                    const getFileType = (url: any) => {
                      const extension = url.split(".").pop().toLowerCase();
                      if (extension === "pdf") {
                        return "pdf";
                      } else if (["xls", "xlsx", "csv"].includes(extension)) {
                        return "excel";
                      } else if (
                        ["jpg", "jpeg", "png", "gif", "webp"].includes(
                          extension
                        )
                      ) {
                        return "image";
                      } else {
                        return "unknown";
                      }
                    };

                    const fileType = getFileType(file);

                    if (fileType === "pdf" || fileType === "excel") {
                      return (
                        <div
                          key={file}
                          className="relative border rounded-md overflow-hidden group w-[100%] h-[150px] bg-gray-100 flex items-center justify-center"
                        >
                          {fileType === "pdf" ? (
                            <FileText size={48} className="text-primary-500" />
                          ) : (
                            <FileSpreadsheet
                              size={48}
                              className="text-primary-500"
                            />
                          )}

                          <div className="absolute bottom-2 left-0 right-0 text-center text-xs text-gray-600 truncate px-2">
                            {file.split("/").pop()}
                          </div>

                          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/70 transition-all duration-500 opacity-0 invisible group-hover:opacity-100 group-hover:visible">
                            {/* <Button
                            className="rounded p-2 text-white border border-white"
                            fillMode={"outline"}
                            onClick={() => handlePreview(file)}
                          >
                            <EyeIcon size={16} className="w-4 h-4" />
                          </Button> */}
                            <Button
                              className="rounded p-2 text-white border border-white"
                              fillMode={"outline"}
                              onClick={() => {
                                // Handle file download here
                                const link = document.createElement("a");
                                link.href = file;
                                link.download = file.split("/").pop() || "file";
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              <Download size={16} className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    }

                    // Default image rendering (existing code)
                    return (
                      <div
                        key={file}
                        className="relative border rounded-md overflow-hidden group w-[100%] h-[150px]"
                      >
                        <img
                          src={file}
                          className="object-cover h-full w-full"
                        />
                        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/70 transition-all duration-500 opacity-0 invisible group-hover:opacity-100 group-hover:visible">
                          <Button
                            className="rounded p-2 text-white border border-white"
                            fillMode={"outline"}
                            onClick={() => handlePreview(file)}
                          >
                            <EyeIcon size={16} className="w-4 h-4" />
                          </Button>
                          <Button
                            className="rounded p-2 text-white border border-white"
                            fillMode={"outline"}
                            onClick={() => {
                              // Handle file download here
                              const link = document.createElement("a");
                              link.href = file;
                              link.download = file.split("/").pop() || "file";
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                          >
                            <Download size={16} className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <></>
              )}

              {taskAction?.status !== SUBMITTED &&
              taskAction?.status !== REASSIGNED &&
              taskAction?.attachmentUrls?.length ? (
                <div className="mt-2">
                  <div className="grid grid-cols-6 gap-3 mt-4">
                    {taskAction?.attachmentUrls.map((file) => {
                      // Function to detect file type from URL
                      const getFileType = (url: any) => {
                        const extension = url.split(".").pop().toLowerCase();
                        if (extension === "pdf") {
                          return "pdf";
                        } else if (["xls", "xlsx", "csv"].includes(extension)) {
                          return "excel";
                        } else if (
                          ["jpg", "jpeg", "png", "gif", "webp"].includes(
                            extension
                          )
                        ) {
                          return "image";
                        } else {
                          return "unknown";
                        }
                      };

                      const fileType = getFileType(file);

                      if (fileType === "pdf" || fileType === "excel") {
                        return (
                          <div
                            key={file}
                            className="relative border rounded-md overflow-hidden group w-[100%] h-[150px] bg-gray-100 flex items-center justify-center"
                          >
                            {fileType === "pdf" ? (
                              <FileText
                                size={48}
                                className="text-primary-500"
                              />
                            ) : (
                              <FileSpreadsheet
                                size={48}
                                className="text-primary-500"
                              />
                            )}

                            <div className="absolute bottom-2 left-0 right-0 text-center text-xs text-gray-600 truncate px-2">
                              {file.split("/").pop()}
                            </div>

                            <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/70 transition-all duration-500 opacity-0 invisible group-hover:opacity-100 group-hover:visible">
                              {/* <Button
                            className="rounded p-2 text-white border border-white"
                            fillMode={"outline"}
                            onClick={() => handlePreview(file)}
                          >
                            <EyeIcon size={16} className="w-4 h-4" />
                          </Button> */}
                              <Button
                                className="rounded p-2 text-white border border-white"
                                fillMode={"outline"}
                                onClick={() => {
                                  // Handle file download here
                                  const link = document.createElement("a");
                                  link.href = file;
                                  link.download =
                                    file.split("/").pop() || "file";
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                              >
                                <Download size={16} className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      }

                      // Default image rendering (existing code)
                      return (
                        <div
                          key={file}
                          className="relative border rounded-md overflow-hidden group w-[100%] h-[150px]"
                        >
                          <img
                            src={file}
                            className="object-cover h-full w-full"
                          />
                          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/70 transition-all duration-500 opacity-0 invisible group-hover:opacity-100 group-hover:visible">
                            <Button
                              className="rounded p-2 text-white border border-white"
                              fillMode={"outline"}
                              onClick={() => handlePreview(file)}
                            >
                              <EyeIcon size={16} className="w-4 h-4" />
                            </Button>
                            <Button
                              className="rounded p-2 text-white border border-white"
                              fillMode={"outline"}
                              onClick={() => {
                                // Handle file download here
                                const link = document.createElement("a");
                                link.href = file;
                                link.download = file.split("/").pop() || "file";
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              <Download size={16} className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <></>
              )}
            </TabStripTab>
          )}
        </TabStrip>
      </div>
    </>
  );

  const primaryActionText = () => {
    return taskAction?.action === "accept"
      ? "Accept"
      : taskAction?.action === "release"
      ? "Submit Release"
      : taskAction?.action === "reassign"
      ? "Accept"
      : "Approve";
  };

  const secondaryActionText = () => {
    return isViewMode
      ? "Close"
      : taskAction?.action === "accept"
      ? "Reject"
      : taskAction?.action === "release"
      ? "Cancel"
      : taskAction?.action === "reassign"
      ? "Reassign"
      : "Reject";
  };

  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [userFilter, setUserFilter] = useState("");
  // Returns filtered users for ComboBox
  function getFilteredUsers(filterValue?: string) {
    const allUsers = users?.data?.filter(x => x.companyId === taskAction?.companyDetails?.id) || [];
    const filter = typeof filterValue === "string" ? filterValue : userFilter;
    if (filter && filter.trim() !== "") {
      return allUsers
        .filter((u) => u.name.toLowerCase().includes(filter.toLowerCase()))
        .sort((u1, u2) => u1.name.localeCompare(u2.name));
    }
    return allUsers.sort((u1, u2) => u1.name.localeCompare(u2.name));
  }

  function deletegateRequest(): void {
    if (!selectedUser) {
      return;
    }

    handleSubmit("delegate");
  }

  const isEngineerPresentInGroup = (dataItem: any) => {
    if (!isAuthorize([ENGINEER]) || !user?.id || !dataItem.groupId) return false;
    return userGroups.some((group) => group.id === dataItem.groupId && group.engineers?.includes(user.id));
  }

  return (
    <>
      <BaseDrawer
        isOpen={showTaskActionDrawer}
        showIcon={true}
        onClose={() => setShowTaskActionDrawer(false)}
        secondaryActionText={secondaryActionText()}
        onSecondaryAction={() => {
          if(taskAction?.action === "accept" && uploadedFiles.length === 0) {
            toast.error("Please attach at least one file before rejecting.");
            return;
          }
          if (isViewMode) {
            setShowTaskActionDrawer(false);
            setShowDetailsDrawer(true);
            return;
          }

          handleSubmit(
            taskAction?.action === "reassign" ? "reassign" : "reject"
          );
        }}
        title={`${selectedArea?.name} ${
          taskAction?.action === "accept" ? "- Assignment" : ""
        }`}
        subtitle={selectedRequest?.refNo}
        primaryActionText={primaryActionText()}
        showPrimaryAction={!isViewMode}
        onPrimaryAction={() => handleSubmit("approve")}
        primaryActionDisabled={!validateSave() || isSubmitting}
        secondaryActionDisabled={
          taskAction?.action === "accept" || taskAction?.action === "action"
            ? !validateSave() || isSubmitting
            : false
        }
        maxW={"70vw"}
        {...drawerProps}
        showBackButton={true}
        onBackButtonClick={() => {
          setShowTaskActionDrawer(false);
          setShowDetailsDrawer(true);
          setTaskAction(null);
          setData([]);
          setUploadedFiles([]);
        }}
        showTertiaryAction={
          taskAction?.action === "accept" &&
          (taskAction?.activities?.length && taskAction?.activities?.length > 1
            ? true
            : false)
        }
        tertiaryActionText="Partially Accept"
        onTertiaryAction={() => handleSubmit("partial")}
        tertiaryActionDisabled={!validateSave() || isSubmitting}
        status={taskAction?.status}
        showCustomAction={!isViewMode || ([ASSIGNED, ACCEPTED, REASSIGNED, OVERDUE].includes(taskAction?.status ?? "") && isAuthorize([ENGINEER]) && (taskAction?.createdBy === user?.id || isEngineerPresentInGroup(taskAction)))}
        customActionText="Delegate"
        onCustomAction={() => {
          setShowDialog(true);
        }}
      >
        {renderDrawerContent()}
      </BaseDrawer>

      {showDialog && (
        <Dialog
          width={450}
          title={"Delegate Request"}
          onClose={() => setShowDialog(false)}
          className="image-dialog overflow-hidden"
        >
          <div className="mb-4">
            <CustomLabel label={"User"} required={true} />
            {/* User */}
            <ComboBox
              data={getFilteredUsers()}
              textField="name"
              dataItemKey="id"
              value={selectedUser || null}
              onChange={(e) => setSelectedUser(e.value || null)}
              placeholder="Select user"
              filterable={true}
              onFilterChange={(e) => {
                setUserFilter(e.filter.value);
                const filteredUsers = getFilteredUsers(e.filter.value);
                if (!filteredUsers.find((u) => u.id === selectedUser?.id)) {
                  setSelectedUser(null);
                }
              }}
            />
          </div>
          <DialogActionsBar>
            <div className="flex justify-end gap-3">
              <div className="w-[86px] h-[34px]">
                <Btn
                  variant="tertiary"
                  text={"Cancel"}
                  border
                  disabled={false}
                  onClick={() => setShowDialog(false)}
                />
              </div>
              <div className="w-[86px] h-[34px]">
                <Btn
                  variant="primary"
                  disabled={!selectedUser}
                  text={"Confirm"}
                  onClick={() => deletegateRequest()}
                />
              </div>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
}

export default TaskActionDrawer;
